import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Monitor, Smartphone } from 'lucide-react';
import { LANGUAGES } from '../../i18n/index.ts';
import { DetailList, PageHeader, pageStyles as s } from '../../components/page.tsx';
import {
  Alert,
  Button,
  ChoiceGroup,
  Dialog,
  DialogActions,
  Notice,
  Panel,
  SelectField,
  TextField,
} from '../../components/ui.tsx';
import { api, ApiError, errorKey } from '../../lib/api.ts';
import { useMe } from '../../lib/auth.ts';
import { ContactSettings } from './ContactSettings.tsx';
import { LogsSection } from './LogsSection.tsx';
import { useTheme, type ThemeChoice } from '../../lib/theme.ts';

interface SessionInfo {
  id: string;
  userAgent: string | null;
  createdAt: string;
  lastUsedAt: string;
  current: boolean;
}

/** Selaimen nimi user-agentista karkeasti: riittää laitteen tunnistamiseen listassa. */
function describeAgent(ua: string | null): { label: string; mobile: boolean } {
  if (!ua) return { label: '?', mobile: false };
  const mobile = /Mobile|Android|iPhone|iPad/.test(ua);
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : '';
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /iPhone|iPad/.test(ua)
      ? 'iOS'
      : /Android/.test(ua)
        ? 'Android'
        : /Mac OS/.test(ua)
          ? 'macOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';
  return { label: [browser, os].filter(Boolean).join(', '), mobile };
}

export function AccountPage() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const { theme, setTheme } = useTheme();
  const queryClient = useQueryClient();
  const [changingEmail, setChangingEmail] = useState(false);
  const [sentTo, setSentTo] = useState<string>();

  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api<{ sessions: SessionInfo[] }>('/auth/sessions').then((r) => r.sessions),
  });
  const revokeOthers = useMutation({
    mutationFn: () => api<void>('/auth/sessions/revoke-others', { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });

  const dateTime = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Europe/Helsinki',
  });

  return (
    <div className={s.stack}>
      <PageHeader title={t('account.title')} />

      <section>
        <h2 className={s.sectionTitle}>{t('account.profile')}</h2>
        <Panel>
          <DetailList rows={[{ label: t('account.email'), value: me?.email }]} />
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.75rem',
              marginTop: '0.75rem',
            }}
          >
            <p style={{ color: 'var(--muted)', fontSize: 'var(--text-sm)', margin: 0 }}>
              {t('account.emailHint')}
            </p>
            <Button onClick={() => setChangingEmail(true)}>{t('emailChange.button')}</Button>
          </div>
          {sentTo && (
            <div style={{ marginTop: '0.75rem' }}>
              <Notice>{t('emailChange.sent', { email: sentTo })}</Notice>
            </div>
          )}
        </Panel>
        {changingEmail && (
          <EmailChangeDialog
            onClose={() => setChangingEmail(false)}
            onSent={(email) => {
              setChangingEmail(false);
              setSentTo(email);
            }}
          />
        )}
      </section>

      <ContactSettings />

      <LogsSection />

      <section>
        <h2 className={s.sectionTitle}>{t('account.preferences')}</h2>
        <Panel>
          <div style={{ display: 'grid', gap: '1.25rem', maxWidth: 420 }}>
            <SelectField
              label={t('topbar.language')}
              value={i18n.language}
              onChange={(e) => void i18n.changeLanguage(e.target.value)}
            >
              {LANGUAGES.map((lng) => (
                <option key={lng} value={lng}>
                  {t(`languages.${lng}`)}
                </option>
              ))}
            </SelectField>
            <ChoiceGroup<ThemeChoice>
              name="theme"
              legend={t('topbar.theme')}
              value={theme}
              onChange={setTheme}
              options={[
                { value: 'system', title: t('topbar.themeSystem') },
                { value: 'light', title: t('topbar.themeLight') },
                { value: 'dark', title: t('topbar.themeDark') },
              ]}
            />
          </div>
        </Panel>
      </section>

      <section>
        <div className={s.sectionHead}>
          <h2 className={s.sectionTitle}>{t('account.sessions')}</h2>
          {sessions.data && sessions.data.length > 1 && (
            <Button onClick={() => revokeOthers.mutate()} disabled={revokeOthers.isPending}>
              {t('account.revokeOthers')}
            </Button>
          )}
        </div>
        {revokeOthers.isSuccess && <Notice>{t('account.revoked')}</Notice>}
        <ul
          className={s.propertyList}
          style={{ marginTop: revokeOthers.isSuccess ? '0.75rem' : 0 }}
        >
          {sessions.data?.map((session) => {
            const agent = describeAgent(session.userAgent);
            const Icon = agent.mobile ? Smartphone : Monitor;
            return (
              <li key={session.id} className={s.propertyRow} style={{ transform: 'none' }}>
                <span className={s.propertyIcon} aria-hidden="true">
                  <Icon size={20} />
                </span>
                <span className={s.propertyText}>
                  <span className={s.propertyName}>{agent.label}</span>
                  <span className={s.propertyMeta}>
                    {t('account.lastUsed', { when: dateTime.format(new Date(session.lastUsedAt)) })}
                  </span>
                </span>
                {session.current && (
                  <span className={`${s.tag} ${s.tagAccent}`}>{t('account.current')}</span>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

/** Uusi osoite: vahvistuslinkki lähtee uuteen osoitteeseen, vaihto tapahtuu vasta vahvistuksella. */
function EmailChangeDialog({
  onClose,
  onSent,
}: {
  onClose: () => void;
  onSent: (email: string) => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string>();
  const request = useMutation({
    mutationFn: (value: string) =>
      api<void>('/auth/email-change', { method: 'POST', body: { email: value } }),
  });

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(undefined);
    const value = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError(t('errors.email'));
      return;
    }
    try {
      await request.mutateAsync(value);
      onSent(value);
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === 'same_email' ? t('emailChange.same') : t(errorKey(e)),
      );
    }
  }

  return (
    <Dialog open onClose={onClose} title={t('emailChange.title')}>
      <form onSubmit={submit} noValidate style={{ display: 'grid', gap: '1.1rem' }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>{t('emailChange.intro')}</p>
        <TextField
          label={t('emailChange.newEmail')}
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {error && <Alert>{error}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={request.isPending}>
            {t('emailChange.send')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
